import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Tooltip, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import React, { useMemo } from 'react';

import { Subtitle, Title } from '../../../components/_mui';
import { FIXED_ITEMS_INFO, getIncludedItemsTitle } from '../../../constants/ui';
import { IPickedMskuItem } from '../../../types/api';
import { ICampaignItem } from '../../../types/pyg';
import { SummaryIncludedRow } from './SummaryIncludedRow';
import { SummaryOptionRow } from './SummaryOptionRow';

interface IProps {
  pickedCount: number;
  itemsCount: number;
  mskuList: ICampaignItem[] | undefined;
  fixedItemsList: ICampaignItem[] | undefined;
  pickedItems: Record<string, IPickedMskuItem>;
  handleViewDetails: (item: ICampaignItem) => void;
  handleOpenAccordion: (internalId: string) => void;
  vendorOf: (itemId?: string) => string | undefined;
  isSingleColumn: boolean;
}

const optionsSectionSx: SxProps<Theme> = { display: 'flex', flexDirection: 'column', gap: 1 };
const includedHeadingSx: SxProps<Theme> = { display: 'flex', flexDirection: 'column', gap: 0.5 };
const includedTitleRowSx: SxProps<Theme> = { display: 'flex', alignItems: 'center', gap: 0.75 };
const infoIconSx: SxProps<Theme> = { fontSize: 18, color: 'action.active', cursor: 'default' };

export const SummarySection = ({
  pickedCount,
  itemsCount,
  mskuList,
  fixedItemsList,
  pickedItems,
  handleViewDetails,
  handleOpenAccordion,
  vendorOf,
  isSingleColumn,
}: IProps) => {
  const mskuItems = useMemo(
    () =>
      mskuList?.map((item) => (
        <SummaryOptionRow
          key={item.internalId}
          item={item}
          pickedOptionId={pickedItems[item.internalId]?.uid}
          onViewDetails={handleViewDetails}
          onOpenAccordion={handleOpenAccordion}
        />
      )),
    [mskuList, pickedItems, handleViewDetails, handleOpenAccordion],
  );

  const fixedItems = useMemo(
    () =>
      fixedItemsList?.map((item) => (
        <SummaryIncludedRow
          key={item.internalId}
          item={item}
          vendor={vendorOf(item.id)}
          onViewDetails={handleViewDetails}
        />
      )),
    [fixedItemsList, handleViewDetails, vendorOf],
  );

  const includedCount = fixedItemsList?.length ?? 0;
  const hasFixedItems = includedCount > 0;
  // A send whose items all resolved to a single variant has nothing to choose, so the progress
  // line would read "0 of 0 complete" above an empty list.
  const hasChoosableItems = !!mskuList?.length;
  const isProgressShown = !isSingleColumn && hasChoosableItems;
  // The desktop rail title already reads "Included items" when that is all the rail holds.
  const isIncludedHeadingShown = isSingleColumn || hasChoosableItems;

  const railSx = useMemo<SxProps<Theme>>(
    () => ({ display: 'flex', flexDirection: 'column', ...(!isSingleColumn && { width: 436, flexShrink: 0 }) }),
    [isSingleColumn],
  );

  const sectionsSx = useMemo<SxProps<Theme>>(
    () => ({ display: 'flex', flexDirection: 'column', gap: 3, ...(isSingleColumn && { marginTop: 3 }) }),
    [isSingleColumn],
  );

  const includedSectionSx = useMemo<SxProps<Theme>>(
    () => ({ display: 'flex', flexDirection: 'column', gap: isSingleColumn ? 2 : 1 }),
    [isSingleColumn],
  );

  return (
    <Box sx={railSx}>
      {!isSingleColumn && <Title>{hasChoosableItems ? 'Your selection' : 'Included items'}</Title>}
      <Box sx={sectionsSx}>
        {isProgressShown && (
          <Box sx={optionsSectionSx}>
            <Subtitle>
              {pickedCount} of {itemsCount} complete
            </Subtitle>
            {mskuItems}
          </Box>
        )}

        {hasFixedItems && (
          <Box sx={includedSectionSx}>
            {isIncludedHeadingShown && (
              <Box sx={includedHeadingSx}>
                <Box sx={includedTitleRowSx}>
                  <Subtitle>{getIncludedItemsTitle(includedCount)}</Subtitle>
                  {!isSingleColumn && (
                    <Tooltip title={FIXED_ITEMS_INFO} placement="top" arrow>
                      <InfoOutlinedIcon tabIndex={0} aria-label={FIXED_ITEMS_INFO} sx={infoIconSx} />
                    </Tooltip>
                  )}
                </Box>
                {isSingleColumn && (
                  <Typography variant="body2" color="text.secondary">
                    {FIXED_ITEMS_INFO}
                  </Typography>
                )}
              </Box>
            )}
            {fixedItems}
          </Box>
        )}
      </Box>
    </Box>
  );
};
